import React from 'react'
import styled from 'styled-components'
import type { PDFCompactListProps } from '../ExportPDFModal/types'

const DEFAULT_ACCENT_COLOR = '#00ADE6'
const DEFAULT_TEXT_COLOR = '#1e293b'
const DEFAULT_SUBTEXT_COLOR = '#64748b'

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 20px 26px;
  box-sizing: border-box;
`

const PageHeader = styled.div<{ $accentColor: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${({ $accentColor }) => $accentColor};
`

const PageTitle = styled.h3<{ $textColor: string }>`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
`

const PageNumber = styled.span<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
`

const Table = styled.table.attrs({
  className: 'print-compact-table',
})`
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
`

const TableHead = styled.thead<{ $accentColor: string }>`
  background: ${({ $accentColor }) => `${$accentColor}10`};
`

const TableHeaderCell = styled.th<{
  $textColor: string
  $width?: string
  $align?: 'left' | 'center' | 'right'
}>`
  padding: 8px 10px;
  text-align: ${({ $align }) => $align || 'left'};
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  ${({ $width }) => $width && `width: ${$width};`}
`

const RowNumberCell = styled.th<{ $textColor: string }>`
  padding: 8px 6px;
  text-align: center;
  font-weight: 600;
  color: ${({ $textColor }) => $textColor};
  border-bottom: 1px solid #e2e8f0;
  width: 32px;
`

const TableBody = styled.tbody``

const TableRow = styled.tr<{ $isEven: boolean }>`
  background: ${({ $isEven }) => ($isEven ? '#fafafa' : 'transparent')};

  &:hover {
    background: #f8fafc;
  }
`

const TableCell = styled.td<{
  $textColor: string
  $align?: 'left' | 'center' | 'right'
}>`
  padding: 8px 10px;
  text-align: ${({ $align }) => $align || 'left'};
  color: ${({ $textColor }) => $textColor};
  border-bottom: 1px solid #f1f5f9;
  line-height: 1.4;
`

const RowNumberData = styled.td<{ $subtextColor: string }>`
  padding: 8px 6px;
  text-align: center;
  color: ${({ $subtextColor }) => $subtextColor};
  border-bottom: 1px solid #f1f5f9;
  font-size: 10px;
  font-weight: 500;
`

const Footer = styled.div`
  margin-top: auto;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
`

const CompanyName = styled.span<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
`

const PageInfo = styled.span<{ $subtextColor: string }>`
  font-size: 10px;
  color: ${({ $subtextColor }) => $subtextColor};
`

/**
 * Compact list component for PDF exports
 * Renders items in a table format with multiple rows per page
 */
export function PDFCompactList<T>({
  items,
  columns,
  getRowData,
  startIndex,
  pageNumber,
  totalPages,
  company,
  accentColor = DEFAULT_ACCENT_COLOR,
  textColor = DEFAULT_TEXT_COLOR,
  subtextColor = DEFAULT_SUBTEXT_COLOR,
}: PDFCompactListProps<T>): React.ReactElement {
  return (
    <PageWrapper>
      <PageHeader $accentColor={accentColor}>
        <PageTitle $textColor={textColor}>
          Items {startIndex} - {startIndex + items.length - 1}
        </PageTitle>
        <PageNumber $subtextColor={subtextColor}>
          Page {pageNumber} of {totalPages}
        </PageNumber>
      </PageHeader>

      <Table>
        <TableHead $accentColor={accentColor}>
          <tr>
            <RowNumberCell $textColor={textColor}>#</RowNumberCell>
            {columns.map((column) => (
              <TableHeaderCell
                key={column.id}
                $textColor={textColor}
                $width={column.width}
                $align={column.align}
              >
                {column.label}
              </TableHeaderCell>
            ))}
          </tr>
        </TableHead>
        <TableBody>
          {items.map((item, index) => {
            const rowData = getRowData(item)
            const rowNumber = startIndex + index
            return (
              <TableRow key={index} $isEven={index % 2 === 1}>
                <RowNumberData $subtextColor={subtextColor}>
                  {rowNumber}
                </RowNumberData>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    $textColor={textColor}
                    $align={column.align}
                  >
                    {rowData[column.id] ?? '-'}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <Footer>
        {company ? (
          <CompanyName $subtextColor={subtextColor}>{company.name}</CompanyName>
        ) : (
          <span />
        )}
        <PageInfo $subtextColor={subtextColor}>
          Page {pageNumber} of {totalPages}
        </PageInfo>
      </Footer>
    </PageWrapper>
  )
}
